import type { ChildProcess } from 'node:child_process';
import { forceKillChild, sigtermAndWaitForExit } from '../process/terminate-child-process.js';
import { cleanupMergedLogDirForServer } from './cleanup-merged-log-dir.js';
import { BRIDGE_SHUTDOWN_GRACE_MS } from './constants.js';
import {
  bumpPreviewEnvironmentSpawnGeneration,
  clearPreviewEnvironmentPoll,
  clearPreviewEnvironmentTails,
  discardPreviewEnvironmentAbortController,
  sendPreviewEnvironmentManagerStatus,
  snapshotPreviewEnvironmentTails,
} from './state.js';
import type { PreviewEnvironmentManagerRuntime } from './runtime.js';

export async function gracefulTerminatePreviewEnvironment(
  runtime: PreviewEnvironmentManagerRuntime,
  environmentId: string,
  proc: ChildProcess,
  graceMs: number,
): Promise<void> {
  const { state, getWs, log } = runtime;
  const shortId = `${environmentId.slice(0, 8)}…`;
  discardPreviewEnvironmentAbortController(state, environmentId);
  await sigtermAndWaitForExit(proc, graceMs, log, shortId);
  if (!state.processes.has(environmentId) || state.processes.get(environmentId) !== proc) {
    return;
  }

  bumpPreviewEnvironmentSpawnGeneration(state, environmentId);
  forceKillChild(proc, log, shortId, graceMs);
  state.processes.delete(environmentId);
  clearPreviewEnvironmentPoll(state, environmentId);
  state.pipedCaptureByServerId.delete(environmentId);
  cleanupMergedLogDirForServer(state.mergedLogCleanupDirByServerId, environmentId);
  const tails = snapshotPreviewEnvironmentTails(state, environmentId);
  clearPreviewEnvironmentTails(state, environmentId);
  sendPreviewEnvironmentManagerStatus(
    getWs,
    environmentId,
    'unknown',
    'Bridge closed before process exited',
    tails,
  );
}

export async function shutdownAllPreviewEnvironments(
  runtime: PreviewEnvironmentManagerRuntime,
  opts?: { graceMs?: number },
): Promise<void> {
  const { state, log } = runtime;
  const graceMs = opts?.graceMs ?? BRIDGE_SHUTDOWN_GRACE_MS;
  const pairs = [...state.processes.entries()];
  if (pairs.length === 0) return;

  log(
    `[preview-environment] Stopping ${pairs.length} local preview environment process${pairs.length === 1 ? '' : 'es'}…`,
  );
  await Promise.all(
    pairs.map(([environmentId, proc]) =>
      gracefulTerminatePreviewEnvironment(runtime, environmentId, proc, graceMs),
    ),
  );
}
